/**
 * Copy for the MFA enrollment modal.
 *
 * Strings shared with the step-up auth phases (`Send code`, `Cancel`) stay in
 * `mfa-messages.ts`; only enrollment-specific copy lives here. Wording is taken
 * from ss-ui-client/src/pages/settings/sign-in-and-security/sections/security/messages.js
 * so the two implementations read identically.
 */

import { defineMessages } from 'react-intl';

export const mfaEnrollmentMessages = defineMessages({
  modalTitle: {
    defaultMessage: 'Multi-Factor Authentication Enrollment',
    id: 'dTYs6n',
    description: 'title of the modal that adds a new multi-factor authentication method',
  },
  selectMethodTitle: {
    defaultMessage: 'Add a New Verification Method',
    id: 't9kIV1',
    description: 'title of the step where a user picks which kind of verification to add',
  },
  selectMethodSubheader: {
    defaultMessage: 'Select the type of verification you want to add to your account.',
    id: 'RANDqq',
    description: 'direction for picking a new verification method',
  },
  methodTotp: {
    defaultMessage: 'Authenticator app',
    id: 'nlwQz5',
    description: 'verification method that reads a code from an authenticator app',
  },
  methodSms: {
    defaultMessage: 'Text message',
    id: 'bfMd6C',
    description: 'verification method that sends a code by text message',
  },
  methodVoice: {
    defaultMessage: 'Phone call',
    id: 'N3BVjF',
    description: 'verification method that reads a code out over an automated phone call',
  },
  methodEmail: {
    defaultMessage: 'Email',
    id: '2mmy+/',
    description: 'verification method that sends a code by email',
  },
  continueButton: {
    defaultMessage: 'Continue',
    id: '57tIr+',
    description: 'button text to advance to the next enrollment step',
  },
  emailTitle: {
    defaultMessage: 'Add Email Verification',
    id: 'MzuolM',
    description: 'title of the step where a user enters the email address to verify',
  },
  emailLabel: {
    defaultMessage: 'Email address',
    id: '4oOyMJ',
    description: 'label for the email address input',
  },
  emailInvalidError: {
    defaultMessage: 'Enter a valid email address',
    id: 'VCg9b8',
    description: 'validation error when the entered email address is not usable',
  },
  phoneTitle: {
    defaultMessage: 'Add Phone Verification',
    id: 'Yg+Wza',
    description: 'title of the step where a user enters the phone number to verify',
  },
  phoneChannelLabel: {
    defaultMessage: 'How would you like to receive your code?',
    id: 'uUiPtD',
    description: 'label for choosing between a text message and a phone call',
  },
  callingCodeLabel: {
    defaultMessage: 'Country code',
    id: '8l36v1',
    description: 'label for the country calling code select',
  },
  callingCodePlaceholder: {
    defaultMessage: 'Select a country',
    id: 'gsAez/',
    description: 'placeholder for the country calling code select',
  },
  phoneLabel: {
    defaultMessage: 'Phone number',
    id: 'PxBXMO',
    description: 'label for the phone number input',
  },
  phoneInvalidError: {
    defaultMessage: 'Enter a valid phone number',
    id: 'IahPBd',
    description: 'validation error when the entered phone number is not usable',
  },
  authenticatorTitle: {
    defaultMessage: 'Add Authenticator App Verification',
    id: 'HFsSrm',
    description: 'title of the step where a user registers an authenticator app',
  },
  authenticatorSubheader: {
    defaultMessage: 'Scan this code with your authenticator app, then continue to enter the code it generates.',
    id: 'pD205v',
    description: 'direction for registering an authenticator app by scanning a QR code',
  },
  authenticatorQrCodeLabel: {
    defaultMessage: 'Authenticator app enrollment QR code',
    id: 'dzr809',
    description: 'accessible label for the QR code image an authenticator app scans',
  },
  secretKeyLabel: {
    defaultMessage: 'Secret key',
    id: 'ckEN2q',
    description: 'label for the authenticator app secret key, entered when a QR code cannot be scanned',
  },
  copySecretKey: {
    defaultMessage: 'Copy this key',
    id: '/kG5sB',
    description: 'button text to copy the authenticator app secret key',
  },
  copiedSecretKey: {
    defaultMessage: 'Copied',
    id: 'kJpWRn',
    description: 'confirmation that the authenticator app secret key was copied',
  },
  listColumnType: {
    defaultMessage: 'Type',
    id: 'IWgXfC',
    description: 'column header for the kind of verification method a row describes',
  },
  listColumnDetails: {
    defaultMessage: 'Details',
    id: 'qCbK0P',
    description: 'column header for the email address or phone number a verification method uses',
  },
  listColumnCreated: {
    defaultMessage: 'Created on',
    id: 'dN6OG9',
    description: 'column header for the date a verification method was added',
  },
  listColumnLastUsed: {
    defaultMessage: 'Last used',
    id: 'gtWXYu',
    description: 'column header for the date a verification method was last used to sign in',
  },
  listColumnAction: {
    defaultMessage: 'Action',
    id: 'AJSyk9',
    description: 'column header for the actions available on a verification method',
  },
  listAddButton: {
    defaultMessage: 'Add new option',
    id: 'OWGEeQ',
    description: 'button text to add another verification method',
  },
  listRemoveButton: {
    defaultMessage: 'Delete',
    id: '3Hf85K',
    description: 'button text to remove a verification method',
  },
  listRemoveBlockedTooltip: {
    defaultMessage: 'Add another verification method before deleting this one.',
    id: 'JurR/A',
    description: 'explanation for why the last remaining verification method cannot be deleted',
  },
  listEnrollmentActive: {
    defaultMessage: 'Enrolled and active',
    id: '8dpmvm',
    description: 'stand-in details for a verification method that has no email address or phone number',
  },
  listNeverUsed: {
    defaultMessage: 'Never',
    id: 'TbY72W',
    description: 'shown instead of a date when a verification method has not been used to sign in yet',
  },
  listEmpty: {
    defaultMessage: 'No verification methods yet.',
    id: 'c+l0tv',
    description: 'shown in place of the verification method table when the account has none',
  },
});
